import { CONFIRMATION_TYPES } from '../../../enums/confirmation-types.enum';
import type { JsonConfirmationModel } from '../../../models/complex/confirmation.model';
import { ConfirmationState } from '../confirmation-collection.state';
import {
    accordionOpenedStatusFromJson,
    complaintsFromJson,
    equipmentFromJson,
    expensesFromJson,
    followupFromJson,
    installationPaperworkFromJson,
    measurementToolsFromJson,
    measuringPointsFromJson,
    participantsFromJson,
    reasonMissingProtocolsFromJson,
    serviceReportFromJson,
    sourceReplacementsFromJson,
    timeRecordingFromJson,
    uploadToDocboxFromJson,
    withAccordionOpened,
} from './confirmation-section.converters';

/**
 * The prefix of the concurrency-store keys the legacy `StorageConfirmationService` wrote, one per
 * confirmation: `CONFIRMATION-<frontId>:<qmNum>:<maNum>-<type>`.
 */
export const CONFIRMATION_CONCURRENT_STORE_KEY = 'CONFIRMATION';

/**
 * A confirmation as the legacy `StorageConfirmationService` wrote it: `JSON.stringify(model)`, the
 * output of `ConfirmationModel.toJSON()`. The sections are optional because the `Json*` interfaces
 * declare every one of them required, which was never true — the model only builds the sections
 * config and confirmation type allow. The identifier is the one field every stored confirmation has.
 */
export type StoredLegacyConfirmation = Partial<JsonConfirmationModel> &
    Pick<JsonConfirmationModel, 'notificationIdentifier'>;

/**
 * Whether a parsed legacy value is a confirmation this conversion can address: without a notification
 * identifier there is nothing to store it under.
 *
 * @param data a parsed value read under a legacy confirmation key
 */
export function isStoredLegacyConfirmation(data: Partial<JsonConfirmationModel>): data is StoredLegacyConfirmation {
    return typeof data.notificationIdentifier?.frontId === 'string' && data.notificationIdentifier.frontId !== '';
}

/**
 * Reads the confirmation type off a legacy key. The type is what follows the last `-`: the identifier
 * before it can hold hyphens of its own, since a `frontId` minted on the device is a UUID.
 *
 * @param key a concurrency-store key
 * @returns the type, or `undefined` when the key is not a legacy confirmation key
 */
export function legacyConfirmationTypeFromKey(key: string): CONFIRMATION_TYPES | undefined {
    if (!key.startsWith(`${CONFIRMATION_CONCURRENT_STORE_KEY}-`)) {
        return undefined;
    }
    const suffix = key.slice(key.lastIndexOf('-') + 1);
    return Object.values(CONFIRMATION_TYPES).find((type) => type === suffix);
}

/**
 * Converts a legacy stored confirmation into the confirmation the slice stores.
 *
 * This is the one conversion from the legacy JSON shape: `modelToConfirmationState` flattens a live
 * model with `JSON.stringify` and hands the result here, and the legacy-data migration hands the
 * value `StorageConfirmationService` stored, which is that same `JSON.stringify(model)`. Working on
 * the JSON rather than through `ConfirmationModelFromJson` keeps the model's collaborators out of it:
 * its constructor needs the notification loaded, value helpers, config and a timesheet, none of which
 * bear on the values stored. Nothing is lost by that — the model still restores from this state when
 * the confirmation is opened, through `confirmationStateToModel`, exactly as the legacy load did.
 *
 * The fields a confirmation is identified and ordered by are not read from the JSON's own
 * `saveTimestamp` or type: `toJSON()` never emitted the type, and its `saveTimestamp` lags behind the
 * write time when a caller saves without first updating the model, so both are passed in.
 *
 * The model's one `accordionOpenedStatus` object is resolved away here: each flag is handed to the
 * section it describes, so a stored section carries everything the slice knows about it. A flag whose
 * section the model never built is dropped, which is what the model's own restore does with it anyway.
 * Anything else in the JSON — a key an older version wrote — is dropped too, since every field is
 * picked explicitly.
 *
 * @param json the legacy confirmation
 * @param confirmationType the confirmation's type
 * @param saveTimestamp the write time to stamp on it
 * @returns the stored confirmation
 */
export function fromJsonConfirmationModel(
    json: StoredLegacyConfirmation,
    confirmationType: CONFIRMATION_TYPES,
    saveTimestamp: number,
): ConfirmationState {
    const identifier = json.notificationIdentifier;
    const accordions = accordionOpenedStatusFromJson(json.accordionOpenedStatus);
    return {
        notificationId: { frontId: identifier.frontId, qmNum: identifier.qmNum, maNum: identifier.maNum },
        confirmationType,
        equipment: withAccordionOpened(equipmentFromJson(json.equipment), accordions.equipment),
        uploadToDocBox: withAccordionOpened(uploadToDocboxFromJson(json.uploadToDocBox), accordions.uploadToDocBox),
        complaints: withAccordionOpened(complaintsFromJson(json.complaints), accordions.complaints),
        followup: withAccordionOpened(followupFromJson(json.followup), accordions.followup),
        measuringPoints: withAccordionOpened(measuringPointsFromJson(json.measuringPoints), accordions.measuringPoints),
        participants: withAccordionOpened(participantsFromJson(json.participants), accordions.participants),
        sourceReplacements: withAccordionOpened(
            sourceReplacementsFromJson(json.sourceReplacements),
            accordions.sourceReplacements,
        ),
        expenses: withAccordionOpened(expensesFromJson(json.expenses), accordions.expenses),
        serviceReport: withAccordionOpened(serviceReportFromJson(json.serviceReport), accordions.serviceReport),
        measurementTool: withAccordionOpened(
            measurementToolsFromJson(json.measurementTool),
            accordions.measurementTool,
        ),
        timeRecording: withAccordionOpened(timeRecordingFromJson(json.timeRecording), accordions.timeRecording),
        installationPaperwork: withAccordionOpened(
            installationPaperworkFromJson(json.installationPaperwork),
            accordions.installationPaperwork,
        ),
        reasonMissingProtocols: reasonMissingProtocolsFromJson(json.reasonMissingProtocols),
        // The slice stores no material data, so the section is its flag and nothing else.
        materials: { accordionOpened: accordions.materials },
        saveTimestamp,
    };
}
